import "./ExportButton.css";

/**
 * Export HTML standalone en lisant directement le DOM
 * Pourquoi : Découplage total de la structure de données interne
 */
class ExportButton {
  constructor() {
    this.isVisible = false;
    this.exportData = null;
  }

  setExportData(data) {
    this.exportData = data;
  }

  show() {
    this.isVisible = true;
    this.updateVisibility();
  }

  hide() {
    this.isVisible = false;
    this.updateVisibility();
  }

  mount(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`Container not found: ${selector}`);
    }

    container.innerHTML = this.render();
    this.bindEvents(container);
  }

  render() {
    return `
      <div class="export-button-container ${this.isVisible ? "visible" : "hidden"}">
        <button class="export-button" id="export-btn" title="Exporter le rapport">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7,10 12,15 17,10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span>Exporter HTML</span>
        </button>
      </div>
    `;
  }

  bindEvents(container) {
    const exportBtn = container.querySelector("#export-btn");
    if (exportBtn) {
      exportBtn.addEventListener("click", () => {
        this.exportReport();
      });
    }
  }

  updateVisibility() {
    const container = document.querySelector(".export-button-container");
    if (container) {
      container.className = `export-button-container ${this.isVisible ? "visible" : "hidden"}`;
    }
  }

  /**
   * Génère et télécharge le rapport HTML
   * Lit directement le DOM au lieu de reconstruire depuis les données
   */
  exportReport() {
    if (!this.exportData) {
      console.warn("Aucune donnée à exporter");
      return;
    }

    const htmlContent = this.generateHTMLFromDOM();
    this.downloadHTML(htmlContent, "rapport-dupscan.html");
  }

  /**
   * Génère le HTML en parcourant le DOM existant
   * Pourquoi : Garantit que l'export correspond exactement à l'affichage
   */
  generateHTMLFromDOM() {
    const timestamp = new Date().toLocaleString("fr-FR");
    const resultsMount = document.getElementById("results-mount");

    if (!resultsMount) {
      console.error("Zone de résultats introuvable");
      return "";
    }

    // Extraire les sections
    const summaryHTML = this.extractSummarySection();
    const warningsHTML = this.extractWarningsSection();
    const clustersHTML = this.extractClustersSection();

    return `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Rapport DupScan - ${timestamp}</title>
    <style>
        ${this.getEmbeddedCSS()}
    </style>
</head>
<body>
    <div class="report-container">
        <header class="report-header">
            <h1>🔍 Rapport d'Analyse DupScan</h1>
            <p class="report-timestamp">Généré le ${timestamp}</p>
        </header>

        <main class="report-content">
            ${summaryHTML}
            ${warningsHTML}
            ${clustersHTML}
        </main>

        <footer class="report-footer">
            <p>Rapport généré par DupScan - Détecteur de doublons pour mesures OTDR</p>
        </footer>
    </div>
</body>
</html>`;
  }

  /**
   * Extrait la section résumé depuis le DOM
   */
  extractSummarySection() {
    const summarySection = document.querySelector(".summary-section");
    if (!summarySection) return "";

    return `<section class="summary-section">${summarySection.innerHTML}</section>`;
  }

  /**
   * Extrait la section warnings depuis le DOM
   */
  extractWarningsSection() {
    const warningsSection = document.querySelector(".warnings-section");
    if (!warningsSection) return "";

    return `<section class="warnings-section">${warningsSection.innerHTML}</section>`;
  }

  /**
   * Extrait la section clusters depuis le DOM
   */
  extractClustersSection() {
    const clustersSection = document.querySelector(".clusters-section");
    if (!clustersSection) return "";

    // Cloner la section pour la modifier sans affecter l'original
    const clonedSection = clustersSection.cloneNode(true);

    // Forcer tous les contenus à être visibles
    const contents = clonedSection.querySelectorAll(".cluster-content");
    contents.forEach((content) => {
      content.classList.add("expanded");
      content.classList.remove("hidden", "collapsed");
      content.style.display = "block";
    });

    // S'assurer que les cartes ne sont pas collapsed
    const cards = clonedSection.querySelectorAll(".cluster-card");
    cards.forEach((card) => {
      card.classList.remove("collapsed", "hidden");
    });

    return `<section class="clusters-section">${clonedSection.innerHTML}</section>`;
  }

  /**
   * CSS embarqué pour le rapport standalone
   * Reprend les styles de l'application pour cohérence visuelle
   */
  getEmbeddedCSS() {
    return `
      /* Reset */
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        line-height: 1.6;
        color: #333;
        background: #f5f5f5;
      }
      
      /* Container */
      .report-container {
        width: 100%;
        margin: 0 auto;
        background: white;
      }
      
      /* Header */
      .report-header {
        background: linear-gradient(135deg, #3498db 0%, #764ba2 100%);
        color: white;
        padding: 2rem;
        text-align: center;
      }
      
      .report-header h1 {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }
      
      .report-timestamp {
        opacity: 0.9;
        font-size: 0.9rem;
      }
      
      /* Content */
      .report-content {
        padding: 2rem;
      }
      
      /* Sections */
      .summary-section, .warnings-section, .clusters-section {
        margin-bottom: 3rem;
      }
      
      .summary-section h2, .warnings-section h2, .clusters-section h2 {
        font-size: 1.5rem;
        margin-bottom: 1.5rem;
        color: #1f2937;
        border-bottom: 2px solid #e5e7eb;
        padding-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      /* Summary Grid */
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1.5rem;
      }
      
      .summary-card {
        background: #f8fafc;
        padding: 1.5rem;
        border-radius: 8px;
        text-align: center;
        border: 1px solid #e2e8f0;
      }
      
      .summary-number {
        font-size: 2rem;
        font-weight: bold;
        color: #3498db;
        margin-bottom: 0.5rem;
      }
      
      .summary-label {
        font-size: 0.9rem;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      /* Warnings */
      .warnings-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      
      .warning-card {
        background: #fef3c7;
        border: 1px solid #f59e0b;
        border-radius: 8px;
        padding: 1rem;
      }
      
      .warning-header {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
      }
      
      .warning-icon {
        color: #f59e0b;
        flex-shrink: 0;
      }
      
      .warning-title {
        font-weight: 600;
        color: #92400e;
        margin-bottom: 0.25rem;
      }
      
      .warning-info {
        color: #78350f;
        font-size: 0.9rem;
      }
      
      /* Clusters */
      .clusters-list {
        display: flex;
        flex-direction: column;
        gap: 2rem;
      }
      
      .cluster-card {
        background: white;
        border: 2px solid #ff9800;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        page-break-inside: avoid;
        margin-bottom: 2rem;
      }
      
      .cluster-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem;
        background: #fff8e1;
        border-bottom: 1px solid #ff9800;
      }
      
      .cluster-info {
        flex: 1;
      }
      
      .cluster-title {
        font-size: 1rem;
        font-weight: 600;
        color: #343a40;
        margin-bottom: 0.25rem;
      }
      
      .cluster-meta {
        display: flex;
        gap: 1rem;
        align-items: center;
      }
      
      .cluster-lambda,
      .cluster-events,
      .cluster-similarity {
        font-size: 0.85rem;
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-weight: 500;
      }
      
      .cluster-lambda {
        background: #e3f2fd;
        color: #1976d2;
      }
      
      .cluster-events {
        background: #f5f5f5;
        color: #495057;
      }
      
      .cluster-similarity {
        background: #ffebee;
        color: #c62828;
      }
      
      /* Cluster Content */
      .cluster-content {
        overflow: visible;
      }
      
      /* Tables */
      .fibers-table-container {
        overflow-x: visible;
        margin-bottom: 0.5rem;
        border-radius: 8px;
        border: 1px solid #e0e0e0;
      }
      
      .fibers-table {
        width: 100%;
        table-layout: auto;
        border-collapse: collapse;
        font-size: 0.85rem;
        background: white;
      }
      
      .fibers-table th,
      .fibers-table td {
        padding: 0.5rem 0.25rem;
        text-align: left;
        border-bottom: 1px solid #e0e0e0;
      }
      
      .fibers-table th {
        background: #f5f5f5;
        font-weight: 600;
        color: #495057;
        white-space: nowrap;
      }
      
      .fibers-table tbody tr:nth-child(even) {
        background: rgba(0, 0, 0, 0.02);
      }
      
      .fibers-table tbody tr:last-child td {
        border-bottom: none;
      }
      
      /* Fiber cells - largeurs fixes */
      .fiber-id {
        width: 290px;
        min-width: 290px;
        max-width: 290px;
        overflow: visible;
        white-space: normal;
        word-break: break-word;
        font-weight: 600;
        color: #3498db;
      }
      
      .fiber-num {
        width: 100px;
        min-width: 100px;
        max-width: 100px;
        text-align: center;
        font-weight: 600;
        color: #3498db;
      }
      
      .fiber-event {
        width: 150px;
        min-width: 150px;
        max-width: 150px;
        text-align: right;
        font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
        color: #495057;
      }
      
      /* Déviations - couleurs exactes */
      .deviation-value {
        font-size: 0.9em;
      }
      
      .deviation-positive {
        color: #006400;
      }
      
      .deviation-negative {
        color: #8B0000;
      }
      
      /* Footer */
      .report-footer {
        background: #f8fafc;
        padding: 1.5rem;
        text-align: center;
        color: #64748b;
        font-size: 0.9rem;
        border-top: 1px solid #e5e7eb;
      }
      
      /* Print */
      @media print {
        body { background: white; }
        .report-container { box-shadow: none; }
        .cluster-card { page-break-inside: avoid; }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `;
  }

  /**
   * Télécharge le contenu HTML
   */
  downloadHTML(content, filename) {
    const blob = new Blob([content], { type: "text/html;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  unmount() {
    this.hide();
    this.exportData = null;
  }
}

export default ExportButton;
